<script lang="ts">
  import {
    computed,
    defineComponent,
    onMounted,
    ref,
    toRefs,
    unref,
    watchEffect,
  } from "vue";
  import { addWeeks, eachDayOfInterval, isValid, parseISO } from "date-fns";

  import { router } from "frontend/router";
  import {
    formatDate,
    DateFormat,
    weekBoundaries,
  } from "shared/utils/date-utils";
  import { useFormCheckbox } from "frontend/uses/use-form-checkbox";
  import {
    AbstractCalendarOptions,
    mainSwitchButtons,
    useAbstractCalendar,
  } from "frontend/uses/use-abstract-calendar";
  import { Cache } from "frontend/utils/request-cache";
  import { Person } from "frontend/interfaces/person";
  import { useWeekView } from "frontend/uses/abstract-view/use-week-view";
  import { useStore as useProfileStore } from "frontend/stores/profile";
  import { Room } from "frontend/interfaces/room";

  import MiniCalendarAsBox from "frontend/components/MiniCalendarAsBox.vue";
  import AbstractCalendarView from "frontend/routes/calendar/AbstractCalendarView.vue";
  import FormSelect from "frontend/components/form/FormSelect.vue";

  export default defineComponent({
    components: {
      MiniCalendarAsBox,
      AbstractCalendarView,
      FormSelect,
    },
    props: {
      day: {
        type: String,
        default: null,
      },
      highlightAppointmentId: {
        type: String,
        default: null,
      },
      person: {
        type: String,
        default: null,
      },
      room: {
        type: String,
        default: null,
      },
    },
    setup(props) {
      const { day, person, room } = toRefs(props);

      const { sortPersons, sortRooms } = useProfileStore();
      const persons = ref<Person[]>([]);
      const loadPersons = async () => {
        const result = await Cache.getCachedPersons();
        persons.value = sortPersons(result);
      };
      const { options: personOptions } = useFormCheckbox(persons, (person) => ({
        id: person.id,
        label: person.name,
      }));

      const rooms = ref<Room[]>([]);
      const loadRooms = async () => {
        const result = await Cache.getCachedRooms();
        rooms.value = sortRooms(result);
      };
      const { options: roomOptions } = useFormCheckbox(rooms, (room) => ({
        id: room.id,
        label: room.name,
      }));

      onMounted(() => {
        loadPersons();
        loadRooms();
      });

      const resourceOptions = computed(() => [
        ...personOptions.value,
        ...roomOptions.value,
      ]);
      const resourceGroupedOptions = computed(() => [
        ...(persons.value.length
          ? [{ label: "Mitarbeiter", options: personOptions.value }]
          : []),
        ...(rooms.value.length
          ? [{ label: "Raum", options: roomOptions.value }]
          : []),
      ]);

      const selectedResource = computed(
        () => person.value || room.value || null
      );
      const onUpdateResource = (resource: string | null) => {
        if (date.value) {
          routeToDate(date.value, resource);
        }
      };

      const computedProperties = {
        dayParsed: computed(() => {
          // try to parse date
          const parsedInitialDate = parseISO(unref(day));
          return isValid(parsedInitialDate) ? parsedInitialDate : null;
        }),

        displayDate: computed(() => {
          const dateUnref = unref(date);
          if (!dateUnref) return "-";
          else {
            const { start, end } = weekBoundaries(dateUnref);
            return `${formatDate(start, DateFormat.CalendarWeek)}, ${formatDate(
              start,
              DateFormat.DateOnly
            )} - ${formatDate(end, DateFormat.DateOnly)}`;
          }
        }),
      };

      const routeToDate = (destination: Date, resource?: string | null) => {
        if (!destination) return;
        const newQuery: Record<string, string> = {};

        if (resource) {
          const isPerson = personOptions.value.some((p) => p.id === resource);
          const isRoom = roomOptions.value.some((r) => r.id === resource);

          if (isRoom) newQuery.room = resource;
          if (isPerson) newQuery.person = resource;
        }

        router.push({
          name: "calendar-week",
          params: {
            day: formatDate(destination, DateFormat.DateOnlyISO),
          },
          query: resource ? newQuery : router.currentRoute.value.query,
        });
      };

      // sanitize routing, if we have no persons locally
      // or we have no person in the query
      watchEffect(() => {
        if (!persons.value.length) return;
        if (props.person || props.room) return;

        if (date.value)
          routeToDate(date.value, sortPersons(persons.value)[0].id);
      });

      const {
        calendar,
        date,
        triggerReload: doTriggerReload,
      } = useWeekView(computedProperties.dayParsed, person, room);
      const options: AbstractCalendarOptions = {
        labelCurrent: "Aktuelle Woche",
        switchButtons: mainSwitchButtons(date),
      };

      return {
        ...computedProperties,

        calendar,
        doTriggerReload,
        options,

        ...useAbstractCalendar(
          date,
          routeToDate,
          (date: Date, directionUp: boolean) =>
            addWeeks(date, directionUp ? 1 : -1)
        ),

        highlightedDays: computed(() =>
          date.value ? eachDayOfInterval(weekBoundaries(date.value)) : []
        ),

        selectedResource,
        resourceOptions,
        resourceGroupedOptions,
        onUpdateResource,
      };
    },
  });
</script>

<template>
  <div class="vue-route__calendar-day-view">
    <AbstractCalendarView
      v-if="calendar"
      v-bind:calendar="calendar"
      v-bind:options="options"
      v-bind:initial-highlight="highlightAppointmentId"
      v-bind:highlighted-days="highlightedDays"
      v-bind:display-date="displayDate"
      context-name="calendar-week-view"
      v-on:refresh-needed="doTriggerReload"
      v-on:navigate:forward="doNavigateForward"
      v-on:navigate:back="doNavigateBack"
      v-on:navigate:current="doNavigateCurrent"
    >
      <template v-slot:topbar>
        <FormSelect
          v-bind:model-value="selectedResource"
          v-bind:groups="resourceGroupedOptions"
          v-bind:options="resourceOptions"
          visual-small
          visual-inverted
          v-on:update:model-value="onUpdateResource"
        />
      </template>
      <template v-slot:sidebar>
        <MiniCalendarAsBox
          v-model="miniCalendarMonth"
          v-bind:selected-days="highlightedDays"
          v-on:date-selected="(dateObj) => doNavigateTo(dateObj.date)"
        />
      </template>
    </AbstractCalendarView>
  </div>
</template>
